import React, { useState } from 'react';
import ClaimStamp, { claimStatus } from './ClaimStamp';

export default function IndexCard({
    book, caller, isYourDrawer,
    onClaim, onUnclaim, onDelete, onSelfStatus, onCopy, onEditSeries, onConvertToSeries, onEditSave,
    draggable, isDragging, isDragOver,
    onDragStart, onDragOver, onDragLeave, onDrop, onDragEnd,
}) {
    const isSeries = book.type === 'series';
    const [open, setOpen] = useState(isSeries);
    const showsClaim = !isYourDrawer;
    const note = (book.note || '').trim();

    const [editingField, setEditingField] = useState(null);
    const [editValue, setEditValue] = useState('');

    const claimedCount = isSeries ? book.seriesBooks.filter(c => c.purchasedBy).length : 0;
    const totalCount = isSeries ? book.seriesBooks.length : 0;

    const idLabel = book.id.slice(0, 8).toUpperCase() + ' · ' + book.addedAt;

    function startEdit(field) {
        setEditingField(field);
        setEditValue(field === 'title' ? book.title : (book.author || ''));
    }

    function commitEdit() {
        const val = editValue.trim();
        const original = editingField === 'title' ? book.title : (book.author || '');
        if (val && val !== original) {
            onEditSave(book.id, { [editingField]: val });
        }
        setEditingField(null);
    }

    function cancelEdit() {
        setEditingField(null);
    }

    const canInlineEdit = isYourDrawer && !isSeries;

    return (
        <li
            className={[
                'idx-card',
                open ? 'is-open' : '',
                isDragging ? 'is-dragging' : '',
                isDragOver ? 'is-drag-over' : '',
                draggable ? 'is-draggable' : '',
            ].filter(Boolean).join(' ')}
            draggable={draggable || undefined}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onDragEnd={onDragEnd}
        >
            {draggable && (
                <span className="drag-handle" aria-label="Drag to reorder" title="Drag to reorder">
                    <span /><span /><span />
                    <span /><span /><span />
                </span>
            )}
            <span className="idx-card-id">{idLabel}</span>

            <div className="idx-card-body">
                <div className="idx-card-type" data-t={book.type}>
                    {isSeries ? `Series · ${totalCount} books` : 'Book'}
                </div>

                <div className="idx-card-title">
                    {canInlineEdit && editingField === 'title' ? (
                        <input
                            className="idx-inline-edit"
                            value={editValue}
                            onChange={e => setEditValue(e.target.value)}
                            onBlur={commitEdit}
                            onKeyDown={e => {
                                if (e.key === 'Enter') commitEdit();
                                if (e.key === 'Escape') cancelEdit();
                            }}
                            autoFocus
                        />
                    ) : (
                        <span
                            className={canInlineEdit ? 'editable-text' : undefined}
                            onClick={canInlineEdit ? () => startEdit('title') : undefined}
                            title={canInlineEdit ? 'Click to edit' : undefined}
                        >
                            {book.title}
                        </span>
                    )}
                </div>

                {canInlineEdit ? (
                    <div className="idx-card-author">
                        {editingField === 'author' ? (
                            <input
                                className="idx-inline-edit idx-inline-edit--author"
                                value={editValue}
                                onChange={e => setEditValue(e.target.value)}
                                onBlur={commitEdit}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') commitEdit();
                                    if (e.key === 'Escape') cancelEdit();
                                }}
                                autoFocus
                            />
                        ) : (
                            <span
                                className="editable-text editable-text--author"
                                onClick={() => startEdit('author')}
                                title="Click to edit"
                            >
                                {book.author || <em style={{ color: 'var(--ink-3)' }}>add author…</em>}
                            </span>
                        )}
                    </div>
                ) : (
                    book.author && <div className="idx-card-author">{book.author}</div>
                )}

                {note && (
                    <div className="idx-card-by">
                        &ldquo;{note}&rdquo;
                        <span className="at">— {book.addedBy}</span>
                    </div>
                )}
                {!isYourDrawer && (
                    <button className="copy-btn" onClick={() => onCopy(book)}>
                        + Copy to my list
                    </button>
                )}
                {isSeries && (
                    <button className="expand-btn" onClick={() => setOpen(v => !v)}>
                        <span>{open ? '▾' : '▸'}</span>
                        <span>{open ? 'collapse' : `show ${totalCount} books`}</span>
                    </button>
                )}
            </div>

            <div className="idx-card-side">
                {showsClaim && !isSeries && (
                    book.selfStatus === 'owned'
                        ? <span className="self-owned-stamp">Owned</span>
                        : <ClaimStamp
                            status={claimStatus(book.purchasedBy, caller)}
                            claimer={book.purchasedBy}
                            onClaim={() => onClaim(book.id)}
                            onUnclaim={() => onUnclaim(book.id)}
                        />
                )}
                {showsClaim && isSeries && (
                    <div className="series-summary">
                        <div className="series-summary-num">
                            {claimedCount}
                            <span className="series-summary-sep">/</span>
                            {totalCount}
                        </div>
                        <div className="series-summary-label">claimed</div>
                    </div>
                )}
                {!showsClaim && (
                    <div className="idx-card-own-actions">
                        <button
                            className={'self-btn self-own-btn' + (book.selfStatus === 'owned' ? ' is-active' : '')}
                            title={book.selfStatus === 'owned' ? 'Mark as TBR' : 'I own this'}
                            onClick={() => onSelfStatus(book.id, 'owned')}
                        >
                            {book.selfStatus === 'owned' ? 'Owned ✓' : 'I Own This'}
                        </button>
                        <button
                            className="self-btn self-read-btn"
                            title="I've read this — remove from list"
                            onClick={() => onSelfStatus(book.id, 'read')}
                        >
                            I've Read This
                        </button>
                        {isSeries && (
                            <button
                                className="self-btn self-edit-series-btn"
                                title="Edit this series"
                                onClick={() => onEditSeries(book)}
                            >
                                Edit Series
                            </button>
                        )}
                        {!isSeries && (
                            <button
                                className="self-btn self-convert-btn"
                                title="Turn this into a series"
                                onClick={() => onConvertToSeries(book)}
                            >
                                Turn into series
                            </button>
                        )}
                        <button className="icon-btn" title="Remove" onClick={() => onDelete(book.id)}>×</button>
                    </div>
                )}
            </div>

            {isSeries && (
                <div className="series-children">
                    <ul className="series-child-list">
                        {book.seriesBooks.map((child, i) => (
                            <li className={'series-child' + (child.selfStatus === 'read' ? ' is-read' : '')} key={i}>
                                <span className="series-child-num">{String(i + 1).padStart(2, '0')}</span>
                                <span className="series-child-title">{child.title}</span>
                                <span className="series-child-side">
                                    {showsClaim && (
                                        child.selfStatus === 'owned'
                                            ? <span className="self-owned-stamp self-owned-stamp--sm">Owned</span>
                                            : child.selfStatus === 'read'
                                                ? <span className="self-read-label">Read</span>
                                                : <ClaimStamp
                                                    status={claimStatus(child.purchasedBy, caller)}
                                                    claimer={child.purchasedBy}
                                                    onClaim={() => onClaim(book.id, i)}
                                                    onUnclaim={() => onUnclaim(book.id, i)}
                                                    compact
                                                />
                                    )}
                                    {!showsClaim && (
                                        <span className="series-child-own-actions">
                                            <button
                                                className={'self-btn self-own-btn self-btn--xs' + (child.selfStatus === 'owned' ? ' is-active' : '')}
                                                onClick={() => onSelfStatus(book.id, 'owned', i)}
                                            >
                                                {child.selfStatus === 'owned' ? 'Owned ✓' : 'Own'}
                                            </button>
                                            <button
                                                className={'self-btn self-read-btn self-btn--xs' + (child.selfStatus === 'read' ? ' is-active' : '')}
                                                onClick={() => onSelfStatus(book.id, 'read', i)}
                                            >
                                                {child.selfStatus === 'read' ? 'Read ✓' : 'Read'}
                                            </button>
                                        </span>
                                    )}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </li>
    );
}
